/* prettier-ignore-file */
/**
 * Address confirmation map, shown after a distance lookup.
 *
 * Ported from the equivalent flow in NewBooking.js (map effect ~496-607, modal
 * ~5314-5375) so the wizard behaves the same way: the customer sees the driving route
 * from the shop to the address they typed, plus the mileage the delivery fee is based
 * on, and explicitly confirms it is right. Autocomplete happily returns a plausible
 * address in the wrong town, and the fee is charged per mile — so a visual check
 * before committing is the point of this screen, not decoration.
 *
 * The map is only mounted while `visible`, and the renderer is detached on close so a
 * stale route never bleeds into the next lookup.
 */

import React, { useEffect, useRef, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  ActivityIndicator,
  ScrollView,
} from "react-native";
import { ensureGoogleMaps } from "../utils/googleMaps";
import { colors } from "../theme";

const AddressValidationModal = ({
  visible,
  address,
  shopAddress,
  miles,
  // True while the distance lookup is in flight. The modal opens before that call
  // completes precisely so it blocks the screen — the RN Modal swallows touches
  // behind it, which is what stops the user editing the address mid-request.
  busy = false,
  lookupError = null,
  onConfirm,
  onReject,
}) => {
  const mapNodeRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const rendererRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!visible || !address) return undefined;

    let cancelled = false;
    let retryTimer = null;
    setLoading(true);
    setError(null);

    const drawRoute = async () => {
      let maps;
      try {
        maps = await ensureGoogleMaps();
      } catch (loadError) {
        if (!cancelled) {
          console.error("❌ Maps failed to load:", loadError);
          setError("Map could not be loaded.");
          setLoading(false);
        }
        return;
      }
      if (cancelled) return;

      // The modal animates in, so the container has no dimensions for a beat. Google
      // Maps renders blank if initialised against a zero-size element.
      const node = mapNodeRef.current;
      if (!node || !node.offsetWidth || !node.offsetHeight) {
        retryTimer = setTimeout(drawRoute, 100);
        return;
      }

      const origin = (shopAddress || "").trim();
      const destination = (address || "").trim();
      if (!origin) {
        setError("No shop address is configured in Settings.");
        setLoading(false);
        return;
      }

      const map = new maps.Map(node, {
        zoom: 12,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
      });
      mapInstanceRef.current = map;

      const renderer = new maps.DirectionsRenderer({ map, suppressMarkers: false });
      rendererRef.current = renderer;

      new maps.DirectionsService().route(
        { origin, destination, travelMode: maps.TravelMode.DRIVING },
        (result, status) => {
          if (cancelled) return;
          if (status !== "OK") {
            console.error("❌ Directions request failed:", status);
            setError("Could not plot a route to that address.");
            setLoading(false);
            return;
          }
          renderer.setDirections(result);

          const bounds = new maps.LatLngBounds();
          result.routes[0].legs.forEach((leg) => {
            bounds.extend(leg.start_location);
            bounds.extend(leg.end_location);
          });
          map.fitBounds(bounds);
          setLoading(false);
          // Nudge the map after layout settles, or it can paint half-drawn.
          setTimeout(() => maps.event.trigger(map, "resize"), 100);
        }
      );
    };

    const initialTimer = setTimeout(drawRoute, 250);

    return () => {
      cancelled = true;
      clearTimeout(initialTimer);
      if (retryTimer) clearTimeout(retryTimer);
      // Detach so a previous route cannot reappear behind the next lookup.
      if (rendererRef.current) {
        rendererRef.current.setMap(null);
        rendererRef.current = null;
      }
      mapInstanceRef.current = null;
    };
  }, [visible, address, shopAddress]);

  const canConfirm = !busy && !lookupError && miles != null;

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onReject}>
      <View style={styles.overlay}>
        <View style={styles.sheet}>
          <Text style={styles.title}>Is this the right address?</Text>
          <Text style={styles.address}>{address}</Text>

          {/* Scrolls only when the viewport is too short for the map plus buttons,
              so the confirm controls stay reachable on small phones. */}
          <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
          <View style={styles.mapContainer}>
            {error ? (
              <View style={styles.mapMessage}>
                <Text style={styles.errorText}>{error}</Text>
              </View>
            ) : (
              <>
                <div
                  ref={mapNodeRef}
                  style={{ width: "100%", height: "100%", backgroundColor: "#DDE3E2" }}
                />
                {loading && (
                  <View style={styles.mapOverlayLoading}>
                    <ActivityIndicator size="large" color={colors.primary} />
                    <Text style={styles.loadingText}>
                      {busy ? "Checking address…" : "Plotting route…"}
                    </Text>
                  </View>
                )}
              </>
            )}
          </View>

          {lookupError ? (
            <Text style={styles.lookupError}>{lookupError}</Text>
          ) : miles != null ? (
            <Text style={styles.distance}>{miles} miles from the shop, each way</Text>
          ) : (
            <Text style={styles.distancePending}>Calculating distance…</Text>
          )}
          </ScrollView>

          <View style={styles.buttonRow}>
            <TouchableOpacity
              dataSet={{ hover: "lift" }}
              style={[styles.button, styles.rejectButton]}
              onPress={onReject}
            >
              <Text style={styles.rejectText}>Not right</Text>
            </TouchableOpacity>
            {/* Confirming commits a per-mile delivery fee, so it stays disabled until
                the distance is actually known — and after a failed lookup there is
                nothing valid to confirm. */}
            <TouchableOpacity
              dataSet={{ hover: "lift" }}
              style={[styles.button, styles.confirmButton, !canConfirm && styles.confirmDisabled]}
              onPress={onConfirm}
              disabled={!canConfirm}
            >
              {busy ? (
                <ActivityIndicator size="small" color={colors.surface} />
              ) : (
                <Text style={[styles.confirmText, !canConfirm && styles.confirmTextDisabled]}>
                  Confirm address
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(15, 26, 25, 0.55)",
    // Centred, not a bottom sheet — it was pinned to the bottom edge of the viewport.
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  sheet: {
    backgroundColor: colors.bg,
    borderRadius: 12, // rounded all round now that it floats rather than sitting on the edge
    padding: 16,
    width: "100%",
    maxWidth: 700,
    // Leaves the overlay padding visible on short phone viewports; the body scrolls
    // if the map plus buttons still cannot fit.
    maxHeight: "85vh",
  },
  body: { flexGrow: 0 },
  title: { fontSize: 17, fontWeight: "700", color: colors.ink900 },
  address: { fontSize: 13, color: colors.ink500, marginTop: 4, marginBottom: 12 },

  bodyContent: { paddingBottom: 2 },
  mapContainer: {
    height: 300,
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: colors.border,
    position: "relative",
  },
  mapMessage: { flex: 1, alignItems: "center", justifyContent: "center", padding: 20 },
  mapOverlayLoading: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.75)",
  },
  loadingText: { marginTop: 10, fontSize: 13, color: colors.ink500 },
  errorText: { fontSize: 13, color: colors.danger, textAlign: "center" },

  distancePending: {
    fontSize: 14,
    color: colors.ink400,
    textAlign: "center",
    marginTop: 12,
  },
  lookupError: {
    fontSize: 14,
    fontWeight: "600",
    color: colors.danger,
    textAlign: "center",
    marginTop: 12,
  },
  distance: {
    fontSize: 15,
    fontWeight: "700",
    color: colors.primary,
    textAlign: "center",
    marginTop: 12,
  },

  buttonRow: { flexDirection: "row", marginTop: 14 },
  button: {
    flex: 1,
    borderRadius: 8,
    paddingVertical: 14,
    minHeight: 48,
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  rejectButton: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    marginRight: 8,
  },
  rejectText: { fontSize: 15, fontWeight: "600", color: colors.ink700 },
  confirmButton: { backgroundColor: colors.primary, marginLeft: 8 },
  confirmText: { fontSize: 15, fontWeight: "600", color: colors.surface },
  confirmDisabled: { backgroundColor: colors.disabled, cursor: "default" },
  confirmTextDisabled: { color: colors.ink400 },
});

export default AddressValidationModal;
